// qs-PerformancePanel.vue
<template>
  <div class="performance-panel">
    <div class="metrics-grid">
      <div
        v-for="metric in metrics"
        :key="metric.id"
        class="metric-item"
      >
        <div class="metric-label">
          {{ metric.label }}
        </div>
        <div class="metric-value">
          {{ metric.value }}
        </div>
        <div class="metric-status">
          {{ metric.status }}
        </div>
      </div>
    </div>
    <div
      v-if="warnings.length > 0"
      class="warnings-section"
    >
      <div class="section-title">
        ⚠️ Warnings
      </div>
      <div class="warnings-list">
        <div
          v-for="warning in warnings"
          :key="warning"
          class="warning-item"
        >
          {{ warning }}
        </div>
      </div>
    </div>
    <div
      v-if="suggestions.length > 0"
      class="suggestions-section"
    >
      <div class="section-title">
        💡 Suggestions
      </div>
      <div class="suggestions-list">
        <div
          v-for="suggestion in suggestions"
          :key="suggestion"
          class="suggestion-item"
        >
          {{ suggestion }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, shallowRef } from 'vue'
import { usePerformanceMonitor } from '@/composables/qs-usePerformanceMonitor'

interface PerformanceData {
  fps: number
  frameTime: number
  memoryUsage: number
  componentRenderTime: number
  composableLoadTime: number
  warnings: string[]
  suggestions: string[]
}

interface Metric {
  id: string
  label: string
  value: string
  status: string
}

const performanceMonitor = usePerformanceMonitor()

const performanceData = shallowRef<PerformanceData>({
  fps: 0,
  frameTime: 0,
  memoryUsage: 0,
  componentRenderTime: 0,
  composableLoadTime: 0,
  warnings: [],
  suggestions: []
})

const formatMemory = (bytes: number): string => {
  if (!bytes || bytes <= 0 || !isFinite(bytes)) return '0B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const sizeIndex = Math.min(i, sizes.length - 1)
  const value = bytes / Math.pow(k, sizeIndex)
  return parseFloat(value.toFixed(1)) + sizes[sizeIndex]
}

const getFpsStatus = (fps: number): string => {
  if (fps >= 55) return 'Excellent'
  if (fps >= 30) return 'Good'
  return 'Poor'
}

const getFrameTimeStatus = (frameTime: number): string => {
  if (frameTime <= 16.67) return 'Excellent'
  if (frameTime <= 33.33) return 'Good'
  return 'Poor'
}

const getMemoryStatus = (memoryUsage: number): string => {
  const mb = memoryUsage / 1024 / 1024
  if (mb <= 50) return 'Low'
  if (mb <= 100) return 'Medium'
  return 'High'
}

const updatePerformanceData = (): void => {
  if (!performanceMonitor) return

  const newFps = performanceMonitor.fps?.value ?? 0
  const newFrameTime = performanceMonitor.frameTime?.value ?? 0
  const newMemoryUsage = performanceMonitor.memoryUsage?.value ?? 0
  const newComponentRenderTime = performanceMonitor.componentRenderTime?.value ?? 0
  const newComposableLoadTime = performanceMonitor.composableLoadTime?.value ?? 0
  const newWarnings = performanceMonitor.warnings?.value ?? []
  const newSuggestions = performanceMonitor.getOptimizationSuggestions?.() ?? []

  if (
    performanceData.value.fps !== newFps ||
    performanceData.value.frameTime !== newFrameTime ||
    performanceData.value.memoryUsage !== newMemoryUsage ||
    performanceData.value.componentRenderTime !== newComponentRenderTime ||
    performanceData.value.composableLoadTime !== newComposableLoadTime ||
    JSON.stringify(performanceData.value.warnings) !== JSON.stringify(newWarnings) ||
    JSON.stringify(performanceData.value.suggestions) !== JSON.stringify(newSuggestions)
  ) {
    performanceData.value = {
      fps: newFps,
      frameTime: newFrameTime,
      memoryUsage: newMemoryUsage,
      componentRenderTime: newComponentRenderTime,
      composableLoadTime: newComposableLoadTime,
      warnings: newWarnings,
      suggestions: newSuggestions
    }
  }
}

const metrics = computed<Metric[]>(() => [
  {
    id: 'fps',
    label: 'FPS',
    value: performanceData.value.fps.toString(),
    status: getFpsStatus(performanceData.value.fps)
  },
  {
    id: 'frameTime',
    label: 'Frame',
    value: `${performanceData.value.frameTime.toFixed(1)}ms`,
    status: getFrameTimeStatus(performanceData.value.frameTime)
  },
  {
    id: 'memory',
    label: 'Memory',
    value: formatMemory(performanceData.value.memoryUsage),
    status: getMemoryStatus(performanceData.value.memoryUsage)
  },
  {
    id: 'render',
    label: 'Render',
    value: `${performanceData.value.componentRenderTime.toFixed(1)}ms`,
    status: 'Component'
  },
  {
    id: 'composable',
    label: 'Composable',
    value: `${performanceData.value.composableLoadTime.toFixed(1)}ms`,
    status: 'Load'
  }
])

const warnings = computed<string[]>(() => performanceData.value.warnings)
const suggestions = computed<string[]>(() => performanceData.value.suggestions)

let updateInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  performanceMonitor.startMonitoringManually()
  updatePerformanceData()
  updateInterval = setInterval(updatePerformanceData, 500)
})

onUnmounted(() => {
  if (updateInterval) {
    clearInterval(updateInterval)
    updateInterval = null
  }
  performanceMonitor.stopMonitoring()
})
</script>

<style scoped>
.performance-panel {
  background: theme('colors.creme.light');
  border-radius: theme('borderRadius.DEFAULT');
  padding: 6px;
  width: 100%;
  max-width: theme('spacing.72'); 
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: theme('fontSize.xs'); 
  line-height: 1.2;
  color: theme('colors.text.DEFAULT');
}

.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: theme('spacing.1'); 
  margin-bottom: 0;
}

.metric-item {
  text-align: left;
  padding: 2px 0; 
}

.metric-label {
  font-size: 8px; 
  color: theme('colors.text.lighter');
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1px; 
}

.metric-value {
  font-size: 10px; 
  font-weight: theme('fontWeight.semibold'); 
  color: theme('colors.text.DEFAULT');
  margin-bottom: 0;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
}

.metric-status {
  font-size: 8px; 
  color: theme('colors.text.lighter');
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.warnings-section,
.suggestions-section {
  margin-top: theme('spacing.1'); 
  padding-top: theme('spacing.1'); 
}

.section-title {
  font-size: 10px; 
  font-weight: theme('fontWeight.semibold'); 
  color: theme('colors.text.DEFAULT');
  margin-bottom: 2px; 
}

.warnings-list,
.suggestions-list {
  display: flex;
  flex-direction: column;
  gap: 1px; 
}

.warning-item,
.suggestion-item {
  font-size: 9px; 
  line-height: 1.2;
  color: theme('colors.text.DEFAULT');
  padding: 1px 0; 
}

.warning-item {
  color: theme('colors.danger.DEFAULT'); 
}

.suggestion-item {
  color: theme('colors.primary.500'); 
}
</style>